import { Typography } from 'antd';
import { StyledStack } from 'app/components/Stack';
import StyledButton from 'app/components/StyledButton';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import React, { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { NumericFormat } from 'react-number-format';
import useWindowSize from 'app/hooks/useWindowSize';
import { getAutoDepositUrl } from 'app/services/authApi';
import useGetApiData from 'app/hooks/useGetApiData';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { isNil } from 'lodash';
import { ReactComponent as CopyIcon } from 'app/assets/img/copy-icon.svg';
import { notify } from 'reapop';

const MAX_DEPOSIT = 499999999;
const MIN_DEPOSIT = 2e3;

const objectToSearchParams = object => {
  return Object.keys(object)
    .map(key => {
      return encodeURIComponent(key) + '=' + encodeURIComponent(object[key]);
    })
    .join('&');
};

export default function PaymentInput() {
  const translations = useSelector(selectTranslations);
  const [amount, setAmount] = useState('');
  const history = useHistory();
  const { device } = useWindowSize();
  const infoUser = useSelector(selectInfoUser);
  const dispatch = useDispatch();
  const qrStatic = infoUser?.businessPartner?.bankInfo?.qrStatic;

  const errMsg = useMemo(() => {
    if (amount === '') return null;

    if (+amount > MAX_DEPOSIT)
      return translations?.['b2b.txt.maxLimitTransaction'] ?? '';
    else if (+amount < MIN_DEPOSIT)
      return translations?.['b2b.txt.minLimitTransaction'] ?? '';
    else return null;
  }, [amount, translations]);

  const { isLoading, handleRequest: handleRequestDeposit } = useGetApiData({
    api: getAutoDepositUrl,
    isSkip: true,
  });

  const handleConfirm = async () => {
    if (!!errMsg || amount === '') return;

    try {
      const formData = new FormData();
      formData.append('amount', amount);
      const res = await handleRequestDeposit(formData);
      if (res) {
        const extraParams = objectToSearchParams({
          ...res?.qrCode,
          ...{ transaction_id: res?.transaction?.id },
        });

        if (isNil(res?.qrCode)) return;

        history.push(
          `/auto-deposit?id=${res?.transaction?.id}&${extraParams}&amount=${res?.transaction?.amount}`,
        );
      }
    } catch (error) {
      console.error('Deposit failed:', error);
    }
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key.toLocaleLowerCase() === 'enter' || e.keyCode === 13)
      handleConfirm();
  };

  const handleDownloadImage = async () => {
    if (!qrStatic) return;

    const link = document.createElement('a');
    link.href = qrStatic;
    link.download = `qr_${infoUser?.businessPartner?.bankInfo?.tid}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInputChange = values => {
    setAmount(values?.value);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <LoadingContainer loading={isLoading}>
      <div>
        <Typography
          style={{
            color: '#090813',
            fontWeight: 700,
            fontSize: '22px',
            lineHeight: '28px',
            marginBottom: 18,
          }}
        >
          {translations?.['b2b.txt.method1.qrPayment']}
        </Typography>
        <StyledStack direction={'column'} rowGap={'32px'}>
          <div className="d-flex flex-column" style={{ width: '100%' }}>
            <Typography
              style={{ color: '#090813', fontWeight: 700, fontSize: '14px' }}
            >
              {translations?.['b2b.autoDeposit.enterAmount']}
            </Typography>
            <InputWrapper>
              <StyledInput
                value={amount}
                placeholder={
                  translations?.['b2b.autoDeposit.enterAmountPlaceHolder']
                }
                width={'100%'}
                suffix="đ"
                thousandSeparator="."
                decimalSeparator=","
                allowNegative={false}
                decimalScale={0}
                allowLeadingZeros={false}
                onValueChange={handleInputChange}
                onKeyDown={handleOnKeyDown}
              />
              {!!errMsg && <ErrMsg>{errMsg}</ErrMsg>}
            </InputWrapper>
          </div>
          <StyledStack justifyContent={'center'}>
            <StyledButton
              onClick={handleConfirm}
              textColor="white"
              disabled={!!errMsg || amount === ''}
              height="46px"
              style={{ minWidth: 170 }}
            >
              {translations?.['b2b.btn.confirm']}
            </StyledButton>
          </StyledStack>
        </StyledStack>
      </div>

      <div style={{ marginTop: 48 }}>
        <Typography
          style={{
            color: '#090813',
            fontWeight: 700,
            fontSize: '22px',
            lineHeight: '28px',
            marginBottom: 18,
          }}
        >
          {
            translations?.[
              qrStatic ? 'b2b.txt.method2.qrPayment' : 'b2b.txt.methodNotQr'
            ]
          }
        </Typography>
        <StyledStack direction={'column'} rowGap={'10px'}>
          {qrStatic && (
            <div className="d-flex flex-column" style={{ width: '100%' }}>
              <Typography
                style={{ color: '#090813', fontWeight: 700, fontSize: '14px' }}
              >
                {translations?.['b2b.txt.qrScan']}
              </Typography>
            </div>
          )}
          <div style={{ width: '100%' }}>
            {qrStatic && (
              <div className="d-flex justify-content-center align-items-center flex-column">
                <img
                  src={qrStatic ?? ''}
                  style={{ maxWidth: 256 }}
                  alt="QR code"
                />
                <StyledButton
                  style={{ minWidth: 146, width: 'fit-content', marginTop: 6 }}
                  onClick={handleDownloadImage}
                  textColor="white"
                >
                  {translations?.['b2b.autoDeposit.downloadQr']}
                </StyledButton>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              {qrStatic && (
                <p style={{ marginBottom: 10 }}>
                  {translations?.['b2b.txt.bankTransfer'] ?? ''}
                </p>
              )}
              <StyledTable>
                <tr>
                  <td>{translations?.['b2b.txt.bank-account'] ?? ''}:</td>
                  <td>
                    <strong>
                      {infoUser?.businessPartner?.bankInfo?.tid ?? ''}
                    </strong>
                    <CopyIcon
                      style={{
                        cursor: 'pointer',
                        width: 16,
                        height: 16,
                        margin: '0px 8px',
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${infoUser?.businessPartner?.bankInfo?.tid ?? ''}`,
                        );
                        dispatch(
                          notify('', 'success', {
                            title: translations?.['b2b.txt.copied'] ?? '',
                            dismissAfter: 2000,
                          }),
                        );
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td>{translations?.['b2b.txt.bank'] ?? ''}:</td>
                  <td>
                    <strong>
                      {infoUser?.businessPartner?.bankInfo?.bankNameLabel ??
                        translations?.['b2b.autoTopup.bankTransfer.bank']}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td>{translations?.['b2b.txt.accountName'] ?? ''}:</td>
                  <td>
                    <strong>
                      {infoUser?.businessPartner?.bankInfo?.bankName ??
                        translations?.[
                          'b2b.autoTopup.bankTransfer.accountName'
                        ]}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td>{translations?.['b2b.autoDeposit.content'] ?? ''}:</td>
                  <td>
                    <strong>
                      {translations?.['b2b.autoTopup.bankTransfer.content'] ??
                        'Top-up'}
                    </strong>
                    <CopyIcon
                      style={{
                        cursor: 'pointer',
                        width: 16,
                        height: 16,
                        margin: '0px 8px',
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${
                            translations?.[
                              'b2b.autoTopup.bankTransfer.content'
                            ] ?? 'Top-up'
                          }`,
                        );
                        dispatch(
                          notify('', 'success', {
                            title: translations?.['b2b.txt.copied'] ?? '',
                            dismissAfter: 2000,
                          }),
                        );
                      }}
                    />
                  </td>
                </tr>
              </StyledTable>
            </div>
          </div>
        </StyledStack>
      </div>

      <NoteArea
        style={{ marginTop: 48 }}
        padding={{ lg: '24px', md: '16px', sm: '8px' }}
        direction={'column'}
      >
        <RegularText
          style={{
            textAlign: 'left',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '150%',
          }}
          dangerouslySetInnerHTML={{
            __html: translations?.['b2b.autoDeposit.note1'] ?? '',
          }}
        />
        <StyledStack
          columnGap={'24px'}
          rowGap={'24px'}
          direction={{ lg: 'row', md: 'column' }}
        >
          <RegularText
            style={{
              fontWeight: 700,
              lineHeight: '150%',
              fontSize: 16,
              textAlign: 'center',
              width: device === 'desktop' ? '50%' : '100%',
            }}
            dangerouslySetInnerHTML={{
              __html: translations?.['b2b.autoDeposit.note2'] ?? '',
            }}
          />
          <RegularText
            style={{
              fontWeight: 700,
              lineHeight: '150%',
              fontSize: 16,
              textAlign: 'center',
              width: device === 'desktop' ? '50%' : '100%',
            }}
            dangerouslySetInnerHTML={{
              __html: translations?.['b2b.autoDeposit.note3'] ?? '',
            }}
          />
        </StyledStack>
      </NoteArea>
    </LoadingContainer>
  );
}

const NoteArea = styled(StyledStack)`
  background-color: #fcf3f3;
  border-radius: 16px;
  gap: 24px;
`;

const StyledInput = styled(NumericFormat)`
  width: 100%;
  border: none;
  border-bottom: 1px solid #d9d9d9;
  padding-bottom: 11px;
  font-size: 20px;
  font-weight: 600;
  color: #000;
  :focus-visible {
    outline: none;
    border-bottom: 1px solid #f06f24;
  }
  ::placeholder {
    font-style: italic;
    font-weight: 400;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 30px;
`;

const ErrMsg = styled.span`
  color: #e55015;
  margin-top: 10px;
  display: inline-block;
  font-size: 16px;
`;

const RegularText = styled.p`
  font-size: 16px;
  color: #000;
  font-weight: 400;
  margin: 0;
`;

const StyledTable = styled.table`
  tr:not(:last-child) td {
    padding-bottom: 10px;
  }
  td:first-child {
    padding-right: 50px;
    @media all and (max-width: 490px) {
      padding-right: 10px;
    }
  }
  td {
    @media all and (max-width: 490px) {
      vertical-align: baseline;
    }
  }
`;
